import api, { strapiCall } from "../../utils/api";
import * as types from "../types/advice_type";

const normalizeFilterValue = (value) => {
  if (value === null || value === undefined) return "";
  return value.toString().trim();
};

const escapeGraphQLString = (value = "") =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r");

const buildArticlesFilterClauses = ({
  categoryId = "",
  subcategoryId = "",
  searchQuery = "",
} = {}) => {
  const filters = [`{ active: { eq: true } }`];

  const normalizedCategoryId = normalizeFilterValue(categoryId);
  const normalizedSubcategoryId = normalizeFilterValue(subcategoryId);
  const normalizedSearchQuery = normalizeFilterValue(searchQuery);

  if (normalizedCategoryId) {
    filters.push(`{
      articleCategories: {
        categoryId: { contains: "${escapeGraphQLString(normalizedCategoryId)}" }
      }
    }`);
  }

  if (normalizedSubcategoryId) {
    filters.push(`{
      articleSubcategories: {
        subcategoryId: { contains: "${escapeGraphQLString(
          normalizedSubcategoryId
        )}" }
      }
    }`);
  }

  if (normalizedSearchQuery) {
    filters.push(`{
      title: { containsi: "${escapeGraphQLString(normalizedSearchQuery)}" }
    }`);
  }

  return filters.join(",\n");
};

const requestArticle =
  (params = {}) =>
  async (dispatch, getState) => {
    const payload = {
      query: `mutation {
        sendArticleRequest(input: {email: "${params.email}", requested_article_title: "${params.articleTitle}"}) {
         success
        }
      }     
    `,
    };

    return api
      .apiCall("/api/v2/graphql", params, payload)
      .then(async (response) => {
        if (response.status === 200) {
          const success = response.data?.data?.sendArticleRequest?.success;
          return { success };
        }
        return { success: false };
      });
  };

const clearPagination =
  (params = {}) =>
  async (dispatch) => {
    return dispatch({ type: types.CLEAR_PAGINATION });
  };

const clearBlogs =
  (params = {}) =>
  async (dispatch) => {
    return dispatch({ type: types.CLEAR_BLOGS });
  };

// NOTE: Strapi i18n/locale support temporarily removed.
// Original locale-aware implementation kept below for future reference.
//
// const getBlogs =
//   (params = {}) =>
//   async (dispatch, getState) => {
//     try {
//       dispatch({ type: types.FETCHING_BLOGS });
//       const categoryId = params?.categoryId;
//       const subcategoryId = params?.subcategoryId;
//       const searchQuery = params?.searchQuery;
//       const start = Math.max(0, params?.start ?? 0);
//       const limit = params?.limit ?? 15;
//       const locale = params?.locale;
//       const filterClauses = buildArticlesFilterClauses({
//         categoryId,
//         subcategoryId,
//         searchQuery,
//       });
//
//       const parseBlogsList = (response) => {
//         if (response?.status !== 200 || response?.data?.errors?.length) {
//           return null;
//         }
//         const articles = response.data?.data?.articles || {};
//         return {
//           meta: articles?.meta || {},
//           data: articles?.data,
//         };
//       };
//
//       const localizedPayload = {
//         query: `
//           query GetBlogs($locale: I18NLocaleCode) {
//             articles(
//               locale: $locale,
//               filters: {
//                 and: [
//                   ${filterClauses}
//                 ]
//               },
//               pagination: {
//                 start: ${start},
//                 limit: ${limit}
//               },
//               sort: "updatedAt:desc"
//             ) {
//               data {
//                 attributes {
//                   author {
//                     data {
//                       attributes {
//                         fullName
//                       }
//                     }
//                   }
//                   coverImage {
//                     data {
//                       attributes {
//                         url
//                       }
//                     }
//                   }
//                   content
//                   title
//                   slug
//                   excerpt
//                   createdAt
//                   updatedAt
//                   metaTitle
//                   metaDescription
//                   articleId
//                   articleCategories {
//                     data {
//                       attributes {
//                         category
//                       }
//                     }
//                   }
//                   articleSubcategories {
//                     data {
//                       attributes {
//                         subcategory
//                       }
//                     }
//                   }
//                 }
//               }
//               meta {
//                 pagination {
//                   total
//                   pageCount
//                   page
//                   pageSize
//                 }
//               }
//             }
//           }
//         `,
//         variables: {
//           locale,
//         },
//       };
//
//       const localizedResponse = await strapiCall(localizedPayload);
//       ...
//     } catch (error) { ... }
//   };

const getBlogs =
  (params = {}) =>
  async (dispatch, getState) => {
    try {
      dispatch({ type: types.FETCHING_BLOGS });
      const categoryId = params?.categoryId;
      const subcategoryId = params?.subcategoryId;
      const searchQuery = params?.searchQuery;
      const start = Math.max(0, params?.start ?? 0);
      const limit = params?.limit ?? 15;
      const filterClauses = buildArticlesFilterClauses({
        categoryId,
        subcategoryId,
        searchQuery,
      });

      const parseBlogsList = (response) => {
        if (response?.status !== 200 || response?.data?.errors?.length) {
          return null;
        }
        const articles = response.data?.data?.articles || {};
        return {
          meta: articles?.meta || {},
          data: articles?.data,
        };
      };

      const localizedPayload = {
        query: `
          query GetBlogs {
            articles(
              filters: {
                and: [
                  ${filterClauses}
                ]
              },
              pagination: {
                start: ${start}, 
                limit: ${limit}
              },
              sort: ["pinToTop:desc", "pinOrder:asc", "updatedAt:desc"]
            ) {
              data {
                attributes {
                  author {
                    data {
                      attributes {
                        fullName
                      }
                    }
                  }
                  coverImage {
                    data {
                      attributes {
                        url
                      }
                    }
                  }
                  content
                  title
                  slug
                  pinToTop
                  pinOrder
                  redirectionLink
                  excerpt
                  createdAt
                  updatedAt
                  metaTitle
                  metaDescription
                  articleId
                  articleCategories {
                    data {
                      attributes {
                        category
                      }
                    }
                  }
                  articleSubcategories {
                    data {
                      attributes {
                        subcategory
                      }
                    }
                  }
                }
              }
              meta {
                pagination {
                  total
                  pageCount
                  page
                  pageSize
                }
              }
            }
          }
        `,
      };

      const localizedResponse = await strapiCall(localizedPayload);

      if (localizedResponse.error || localizedResponse?.data?.errors?.length) {
        return dispatch({
          type: types.FETCHING_BLOGS_FAILED,
          payload: {
            error:
              localizedResponse?.error?.message ||
              localizedResponse?.data?.errors?.[0]?.message ||
              "Failed to fetch blogs",
          },
        });
      }

      const parsedResponse = parseBlogsList(localizedResponse);
      if (!parsedResponse) {
        return dispatch({
          type: types.FETCHING_BLOGS_FAILED,
          payload: { error: "No data received from API" },
        });
      }

      if (localizedResponse.status === 200 || parsedResponse) {
        const { meta, data } = parsedResponse;

        if (!data) {
          return dispatch({
            type: types.FETCHING_BLOGS_FAILED,
            payload: { error: "No data received from API" },
          });
        }

        let newArticles = [];
        const currentArticles = getState().advice?.blogs || [];
        const isLoadingMore = start > 0 && currentArticles.length > 0;

        if (isLoadingMore) {
          // Append new articles for pagination
          newArticles = [...currentArticles, ...data];
        } else {
          // First load or filter change - replace articles
          newArticles = data;
        }

        const currentPage = Math.floor(start / limit) - 1;

        return dispatch({
          type: types.FETCHING_BLOGS_SUCCEEDED,
          payload: {
            newArticles,
            meta: {
              ...meta,
              page: currentPage, // Calculate current page
            },
          },
        });
      }

      // If we reach here, something went wrong with the response
      dispatch({
        type: types.FETCHING_BLOGS_FAILED,
        payload: {
          error: `Invalid response status: ${localizedResponse.status}`,
        },
      });
    } catch (error) {
      dispatch({
        type: types.FETCHING_BLOGS_FAILED,
        payload: { error: error.message || "Failed to fetch blogs" },
      });
    }
  };

const getArticleCategories =
  (params = {}) =>
  async (dispatch, getState) => {
    try {
      dispatch({ type: types.FETCHING_CATEGORIES });
      const payload = {
        query: `
          query {
            articleCategories(sort: "order:asc") {
              data {
                attributes {
                  categoryId
                  category
                  articleSubcategories(sort: "subcategory:asc"
                  pagination: { limit: 1000 }) {
                    data {
                      attributes {
                        subcategory
                        subcategoryId
                      }
                    }
                  }
                }
              }
            }
          }
        `,
      };

      const response = await strapiCall(payload, true);
      if (response.status === 200) {
        const { data } = response.data?.data?.articleCategories || [];
        if (data) {
          return dispatch({
            type: types.FETCHING_CATEGORIES_SUCCEEDED,
            payload: data,
          });
        }
      }
    } catch (error) {}
  };

const getArticleSubCategories =
  (params = {}) =>
  async (dispatch, getState) => {
    try {
      dispatch({ type: types.FETCHING_SUBCATEGORIES });
      const payload = {
        query: `
          query {
            articleSubcategories(sort: "subcategory:asc"
            pagination: { limit: 1000 }) {
              data {
                attributes {
                  subcategory
                  subcategoryId
                }
              }
            }
          }
        `,
      };

      const response = await strapiCall(payload, true);
      if (response.status === 200) {
        const { data } = response.data?.data?.articleSubCategories || [];
        if (data) {
          return dispatch({
            type: types.FETCHING_SUBCATEGORIES_SUCCEEDED,
            payload: data,
          });
        }
      }
    } catch (error) {}
  };

// NOTE: Original locale-aware version kept for future re-enable.
// const getSingleBlog = async (slug, locale) => {
//   try {
//     const parseSingleBlog = (response) => {
//       if (response?.status !== 200) return null;
//       const { data } = response.data?.data?.articles || [];
//       if (data) return data[0] || null;
//       return null;
//     };
//
//     const buildLocalizedPayload = (queryLocale) => ({
//       query: `
//         query GetSingleBlog($slug: String!, $locale: I18NLocaleCode) {
//           articles(locale: $locale, filters: { slug: { eq: $slug } }) {
//             data { ... }
//           }
//         }
//       `,
//       variables: { slug, locale: queryLocale },
//     });
//
//     const localizedPayload = buildLocalizedPayload(locale);
//     const localizedResponse = await strapiCall(localizedPayload, true);
//     if (localizedResponse?.error) return null;
//     const localizedBlog = parseSingleBlog(localizedResponse);
//     if (localizedBlog) return localizedBlog;
//
//     const alternateLocale = locale === "ms" ? "en" : "ms";
//     if (alternateLocale === locale) return null;
//     const alternateResponse = await strapiCall(
//       buildLocalizedPayload(alternateLocale),
//       true
//     );
//     if (alternateResponse?.error) return null;
//     return parseSingleBlog(alternateResponse);
//   } catch (error) {
//     return null;
//   }
// };

const getSingleBlog = async (slug) => {
  try {
    const parseSingleBlog = (response) => {
      if (response?.status !== 200) return null;
      const { data } = response.data?.data?.articles || [];
      return data ? data[0] || null : null;
    };

    const payload = {
      query: `
        query GetSingleBlog($slug: String!) {
          articles(filters: { slug: { eq: $slug } }) {
            data {
              attributes {
                author {
                  data {
                    attributes {
                      fullName
                    }
                  }
                }
                coverImage {
                  data {
                    attributes {
                      url
                    }
                  }
                }
                content
                title
                slug
                excerpt
                createdAt
                updatedAt
                metaTitle
                metaDescription
                articleCategories {
                  data {
                    attributes {
                      category
                    }
                  }
                }
                articleSubcategories {
                  data {
                    attributes {
                      subcategory
                    }
                  }
                }
                seo {
                  metaTitle
                  metaDescription
                  metaImage {
                    data {
                      attributes {
                        url
                      }
                    }
                  }
                  metaRobots
                  canonicalURL
                  keywords
                  metaSocial {
                    socialNetwork
                    title
                    description
                    image {
                      data {
                        attributes {
                          url
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables: { slug },
    };

    const response = await strapiCall(payload, true);
    if (response?.error) return null;
    return parseSingleBlog(response);
  } catch (error) {
    return null;
  }
};

const getAdvicePageAd = async (locale) => {
  const parseAdvicePageAd = (response) => {
    if (response.status !== 200) return null;

    const { data } =
      response.data?.data?.malaysia?.data?.attributes?.advicePageAd || {};

    return data || null;
  };

  const localizedPayload = {
    query: `
      query AdvicePageAd {
        malaysia {
          data {
            attributes {
              advicePageAd {
                data {
                  attributes {
                    title
                    url
                    ctaButtonText
                    image {
                      data {
                        attributes {
                          alternativeText
                          url
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
  };

  const localizedResponse = await strapiCall(localizedPayload);
  return parseAdvicePageAd(localizedResponse);
};

const updateSearchQuery =
  (params = "") =>
  async (dispatch, getState) => {
    return dispatch({
      type: types.UPDATE_SEARCH_QUERY,
      payload: params,
    });
  };

const updateStaticSearchQuery =
  (params = "") =>
  async (dispatch, getState) => {
    return dispatch({
      type: types.UPDATE_STATIC_SEARCH_QUERY,
      payload: params,
    });
  };

const updateSelectedCategory =
  (params = {}) =>
  async (dispatch, getState) => {
    return dispatch({
      type: types.UPDATE_SELECTED_CATEGORY,
      payload: params,
    });
  };

const updateSelectedSubcategory =
  (params = {}) =>
  async (dispatch, getState) => {
    return dispatch({
      type: types.UPDATE_SELECTED_SUBCATEGORY,
      payload: params,
    });
  };

/*
 * Params:
 * 1) canCancel
 * 2) cancelToken
 */
const getBlogsForSitemap = async (params = {}) => {
  const advice_page = true;
  const payload = {
    query: `query getBlogsAdvicePage {
      posts(first: 20) {
        edges {
          node {
            title
            id
            slug
          }
        }
      }
    }`,
  };

  const response = await api.apiCall(
    "/graphql",
    params,
    payload,
    null,
    advice_page
  );
  if (response.status === 200) {
    const blogs = response.data?.data?.posts?.edges;

    if (blogs != null) {
      return blogs;
    }
    if (blogs === null) {
      return null;
    }
    return null;
  }
};

const getBlogsWithoutRedux = async (params = {}) => {
  const filterClauses = buildArticlesFilterClauses({
    categoryId: params?.categoryId,
    subcategoryId: params?.subcategoryId,
    searchQuery: params?.searchQuery,
  });

  const parseBlogsList = (response) => {
    if (response?.status !== 200 || response?.data?.errors?.length) return null;
    const { meta } = response.data?.data?.articles || {};
    const { data } = response.data?.data?.articles || null;

    if (data != null) {
      return { data, meta };
    }

    if (data === null) {
      return { blogNotFound: true, meta };
    }

    return null;
  };

  const localizedPayload = {
    query: `
          query GetBlogsWithoutRedux {
            articles(
              filters: {
                and: [
                  ${filterClauses}
                ]
              },
              pagination: {
               start: ${params?.start ?? 0}, limit: ${params?.limit ?? 15}
              },
              sort: "updatedAt:desc"
            ) {
              data {
                attributes {
                  author {
                    data {
                      attributes {
                        fullName
                      }
                    }
                  }
                  coverImage {
                    data {
                      attributes {
                        url
                      }
                    }
                  }
                  content
                  title
                  slug
                  excerpt
                  createdAt
                  updatedAt
                  metaTitle
                  metaDescription
                  articleId
                  articleCategories {
                    data {
                      attributes {
                        category
                      }
                    }
                  }
                  articleSubcategories {
                    data {
                      attributes {
                        subcategory
                      }
                    }
                  }
                }
              }
              meta {
                pagination {
                  total
                  pageCount
                  page
                  pageSize
                }
              }
            }
          }
        `,
  };

  const localizedResponse = await strapiCall(localizedPayload);
  const parsedResponse = parseBlogsList(localizedResponse);
  return parsedResponse || { blogNotFound: true };
};

const getArticlesForSitemap = async (params = {}) => {
  const payload = {
    query: `
      query {
        articles(
          filters: { active: { eq: true } },
          pagination: {
            start: ${params?.start ?? 0}, 
            limit: ${params?.limit ?? 1000}
          },
          sort: "updatedAt:desc"
        ) {
          data {
            attributes {
              slug
              updatedAt
              createdAt
              metaTitle
              metaDescription
            }
          }
        }
      }
    `,
  };

  const response = await strapiCall(payload);

  if (response.status === 200) {
    const articles = response.data?.data?.articles?.data;
    return articles ?? [];
  }

  return [];
};

export {
  clearBlogs,
  clearPagination,
  getAdvicePageAd,
  getArticleCategories,
  getArticlesForSitemap,
  getArticleSubCategories,
  getBlogs,
  getBlogsForSitemap,
  getBlogsWithoutRedux,
  getSingleBlog,
  requestArticle,
  updateSearchQuery,
  updateSelectedCategory,
  updateSelectedSubcategory,
  updateStaticSearchQuery,
};
